"use client";

import * as React from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "next-themes";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export function ThemeSelector() {
  const { theme, setTheme } = useTheme();
  const { t } = useI18n();
  const [mounted, setMounted] = React.useState(false);

  // Avoid hydration mismatch
  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const themes = [
    {
      value: "light",
      label: t("settings.theme.light", "Light"),
      icon: Sun,
      description: t("settings.theme.lightDesc", "Light color theme"),
    },
    {
      value: "dark",
      label: t("settings.theme.dark", "Dark"),
      icon: Moon,
      description: t("settings.theme.darkDesc", "Dark color theme"),
    },
    {
      value: "system",
      label: t("settings.theme.system", "System"),
      icon: Monitor,
      description: t("settings.theme.systemDesc", "Follow system preference"),
    },
  ];

  return (
    <div className="space-y-2">
      {themes.map((item) => {
        const Icon = item.icon;
        const isActive = theme === item.value;

        return (
          <button
            key={item.value}
            onClick={() => setTheme(item.value)}
            className={cn(
              "w-full flex items-center gap-4 p-2 rounded-xl border transition-all duration-200",
              "hover:bg-muted/50 active:scale-[0.99]",
              isActive
                ? "border-primary/50 bg-primary/5 shadow-sm"
                : "border-border bg-card"
            )}
          >
            <div
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-full transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              )}
            >
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <div className={cn(
                "font-medium text-base",
                isActive ? "text-foreground" : "text-foreground/80"
              )}>
                {item.label}
              </div>
              <div className="text-sm text-muted-foreground line-clamp-1">
                {item.description}
              </div>
            </div>
            <div className={cn(
              "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors",
              isActive ? "border-primary bg-primary" : "border-muted-foreground/30"
            )}>
              {isActive && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

